import { supabase, DEFAULT_ORG_ID, applyOrgFilter, getActiveOrgOverride, getActiveOrgId, signImagesDeep } from '../supabaseClient'
import { getCurrentStaff } from './auth'
import { canCreate } from './billing'
import { invocarFuncion } from './funciones'
import { MENSAJE_SIN_FILA } from '../staffEstado'

const conMensaje = (message, code) => Object.assign(new Error(message), code ? { code } : {})
const hoyISO = () => new Date().toISOString().slice(0, 10)

// La pantalla de Staff del club: TODAS las filas de membresia con la org (activas o no), con rol y estado de la membresia, la
// foto firmada y si la persona tambien esta activa en otra org (get_org_staff, 20261031_cada_org_su_staff). No lee staff por
// org_id: eso solo trae a quien tiene al club de principal.
export const getOrgStaff = async (orgId) => {
  if (!orgId) return { data: null, error: conMensaje('No se pudo determinar el club.') }
  try {
    const { data, error } = await supabase.rpc('get_org_staff', { p_org_id: orgId })
    if (error) return { data: null, error }
    const filas = (data || []).map((f) => ({ ...f, id: f.staff_id, imagen_url: null }))
    await signImagesDeep(filas)
    return { data: filas, error: null }
  } catch (error) {
    console.error('[getOrgStaff]', error)
    return { data: null, error }
  }
}

// Dar de baja / reactivar EN ESTE CLUB: la membresia (persona, org), no staff.activo (que es global y la sacaba de todas sus
// orgs). Igual que setMembershipStatus de Torneos. Sin filas = la RLS no la dejo (no es admin, o la fila no existe).
const setMembershipStatus = async (staffId, orgId, status) => {
  try {
    const { data, error } = await supabase
      .from('staff_club_memberships')
      .update({ status, left_at: status === 'inactive' ? hoyISO() : null, updated_at: new Date().toISOString() })
      .eq('staff_id', staffId)
      .eq('org_id', orgId)
      .select('staff_id, status')
    if (error) throw error
    if (!data || data.length === 0) {
      return { data: null, error: conMensaje('el cambio no se guardó; recarga la lista e intenta de nuevo.', 'SIN_FILA') }
    }
    return { data: data[0], error: null }
  } catch (error) {
    console.error('[setMembershipStatus]', error)
    return { data: null, error }
  }
}

export const darDeBajaEnClub = (staffId, orgId) => setMembershipStatus(staffId, orgId, 'inactive')
export const reactivarEnClub = (staffId, orgId) => setMembershipStatus(staffId, orgId, 'active')

export const uploadStaffImage = async (staffId, { image_base64, filename }) => {
  try {
    if (!staffId || !image_base64) {
      throw new Error('Staff ID and image data are required');
    }

    const { data: result, error } = await supabase.functions.invoke('upload-player-image', {
      body: {
        staff_id: staffId,
        image_base64: image_base64,
        filename: filename || `${staffId}_${Date.now()}.jpg`,
        content_type: 'image/jpeg'
      },
    });

    if (error) throw error;
    if (result?.error) {
      return { data: null, error: result.error };
    }
    return { data: result, error: null };
  } catch (error) {
    console.error('Upload staff image error:', error);
    return { data: null, error: error.message };
  }
};

export const removeStaffImage = async (staffId) => {
  try {
    if (!staffId) {
      throw new Error('Staff ID is required');
    }

    const { data: result, error } = await supabase.functions.invoke('upload-player-image', {
      body: { staff_id: staffId, remove: true },
    });

    if (error) throw error;
    if (result?.error) {
      return { data: null, error: result.error };
    }
    return { data: result, error: null };
  } catch (error) {
    console.error('Remove staff image error:', error);
    return { data: null, error: error.message };
  }
};

// Get all staff (coaches, managers, etc.) - ONLY ACTIVE
export const createStaff = async (staffData) => {
  try {
    const orgId = getActiveOrgOverride() || staffData.org_id || (await getCurrentStaff())?.data?.org_id || DEFAULT_ORG_ID

    if (!(await canCreate(orgId, 'staff'))) {
      return { data: null, error: { code: 'PLAN_LIMIT', resource: 'staff', message: 'Alcanzaste el límite de staff de tu plan. Mejora tu plan para agregar más.' } }
    }

    // Por RPC y no con un INSERT directo: el acceso se resuelve por
    // staff_club_memberships, NO por staff.org_id. Un INSERT que solo llenaba `staff`
    // dejaba a la persona listada como miembro del club pero sin poder entrar -- veia
    // "Acceso no autorizado" sin explicacion. Paso de verdad con tres personas.
    //
    // upsert_org_member crea la persona Y su membresia, y ademas resuelve el caso del
    // correo que YA existe en otra org (un coach que trabaja en dos clubes): en vez de
    // fallar por email duplicado, le agrega la membresia de esta org. Es el mismo
    // camino que usa el alta de club.
    const { data: filas, error } = await supabase.rpc('upsert_org_member', {
      p_org_id: orgId,
      p_email: String(staffData.email || '').toLowerCase().trim(),
      p_nombre: staffData.nombre_completo,
      p_telefono: staffData.telefono || null,
      p_rol: staffData.rol || 'coach',
      p_is_founder: false,
    })
    if (error) throw error

    const fila = Array.isArray(filas) ? filas[0] : filas
    if (fila?.outcome === 'already_member') {
      return { data: null, error: { code: 'YA_ES_MIEMBRO', message: 'Esa persona ya forma parte del staff de este club.' } }
    }

    // El RPC devuelve el id; la pantalla espera la fila completa.
    const { data, error: leerError } = await supabase
      .from('staff').select('*').eq('id', fila?.staff_id).single()
    if (leerError) throw leerError

    return { data, error: null, reused: fila?.outcome !== 'created_new' }
  } catch (error) {
    console.error('[createStaff]', error)
    return { data: null, error }
  }
}

// Sin filas (la RLS no deja: la persona ya no tiene membresia activa en el club) es un error claro, no el PGRST116 tecnico.
export const updateStaff = async (staffId, updates) => {
  try {
    const { data, error } = await supabase
      .from('staff')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', staffId)
      .select()
      .maybeSingle()

    if (error) {
      if (error.code === 'PGRST116') return { data: null, error: conMensaje(MENSAJE_SIN_FILA, 'SIN_FILA') }
      throw error
    }
    if (!data) return { data: null, error: conMensaje(MENSAJE_SIN_FILA, 'SIN_FILA') }
    return { data, error: null }
  } catch (error) {
    console.error('[updateStaff]', error)
    return { data: null, error }
  }
}

// "Cuenta desactivada" (staff.activo = false): el club reactiva solo si la persona no esta activa en otra org (si lo esta,
// el trigger de la base responde que solo MYKLUBB puede).
// Solo staff.activo: estatus (activo, por_confirmar, baja) no se toca, para no pisar un por_confirmar.
export const reactivarEnMyklubb = (staffId) => updateStaff(staffId, { activo: true })

// Las listas de staff de las demas pantallas (selectores de coach, inventario, bienvenida): quien tiene membresia ACTIVA en la org
// activa (la del override, si un Platform Admin la tiene) y staff.activo = true. El rol es el de esa membresia, no staff.rol (que
// es el de la org principal). Antes filtraban por staff.org_id (la principal) y staff.activo: salian quienes estaban de baja en el
// club y faltaba quien entro desde otra org. Siguen devolviendo filas de staff (con rol = el de la membresia, sin el embed).
const STAFF_CON_MEMBRESIA = '*, staff_club_memberships!inner(org_id, status, rol)'

export const getAllStaff = async (rol = null) => {
  try {
    const orgId = getActiveOrgOverride() || getActiveOrgId();
    let query = supabase
      .from('staff')
      .select(STAFF_CON_MEMBRESIA)
      .eq('activo', true)
      .eq('staff_club_memberships.status', 'active')
      .order('nombre_completo');

    // Sin org (no deberia pasar detras del login) la RLS de staff_club_memberships ya clava a la org activa.
    if (orgId) query = query.eq('staff_club_memberships.org_id', orgId);

    if (Array.isArray(rol) && rol.length > 0) {
      query = query.in('staff_club_memberships.rol', rol);
    } else if (rol) {
      query = query.eq('staff_club_memberships.rol', rol);
    }

    const { data, error } = await query;
    if (error) return { data: null, error };
    const filas = (data || []).map(({ staff_club_memberships: membresias, ...persona }) => {
      const membresia = Array.isArray(membresias) ? membresias[0] : membresias;
      return { ...persona, rol: membresia?.rol ?? persona.rol };
    });
    await signImagesDeep(filas);
    return { data: filas, error: null };
  } catch (error) {
    console.error('Error fetching staff:', error);
    return { data: null, error };
  }
};

// Get all staff including inactive (for admin management)
export const getAllStaffIncludingInactive = async (rol = null) => {
  try {
    let query = supabase
      .from('staff')
      .select('*')
      .order('nombre_completo');

    // Filter by role if specified
    if (rol) {
      query = query.eq('rol', rol);
    }

    query = applyOrgFilter(query);

    const { data, error } = await query;
    if (error) return { data: null, error };
    await signImagesDeep(data);
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching staff:', error);
    return { data: null, error };
  }
};

// "Eliminar acceso" (delete-staff-auth) ya no se ofrece en el Club: borrar el usuario de Auth choca con la FK
// coaches_auth_user_id_fkey (NO ACTION) y falla siempre con 23503 (revision de 20261031_cada_org_su_staff).

// update-staff-role escribe la membresia; sus 409 (sin membresia activa, fundador) llegan con su mensaje.
export const updateStaffPlatformRole = async (staffId, newRole) => {
  const { data, error } = await invocarFuncion('update-staff-role', { staff_id: staffId, new_role: newRole },
    'No pudimos actualizar el rol.')
  if (error) {
    console.error('Error updating staff role:', error)
    return { data: null, error }
  }
  return { data: data?.staff ?? null, error: null }
};

// Por supabase.functions.invoke (el cliente manda x-active-org; el fetch crudo de antes no lo mandaba).
// update-staff-email cambia staff.email ANTES que el correo de la cuenta: si falla la cuenta ("No se pudo actualizar el correo de
// acceso"), la ficha ya tiene el correo nuevo. Ese caso lleva code = 'FICHA_SIN_ACCESO'.
export const MENSAJE_ACCESO_SIN_CAMBIO = 'No se pudo actualizar el correo de acceso'
export const updateStaffEmail = async (staffId, email) => {
  const { data, error } = await invocarFuncion('update-staff-email', { staff_id: staffId, email }, 'No pudimos actualizar el correo.')
  if (error) {
    console.error('Error updating staff email:', error)
    if (error.message === MENSAJE_ACCESO_SIN_CAMBIO) error.code = 'FICHA_SIN_ACCESO'
  }
  return { data, error }
};

// Backward compatibility - get all coaches (only active)
export const getAllCoaches = async () => {
  return await getAllStaff(['head_coach', 'coach', 'manager', 'assistant']);
};

// Get all managers
export const getAllManagers = async () => {
  return await getAllStaff('manager');
};
